'use client';

import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useMonitoringGraph } from '@/hooks/useMonitoringGraph';
import { useGraphTooltip } from '@/hooks/useGraphTooltip';

interface MonitoringGraphProps {
  className?: string;
}

export const MonitoringGraph = ({ className = '' }: MonitoringGraphProps) => {
  const { data, generateSvgPath, generateAreaPath } = useMonitoringGraph();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 400, height: 150 });

  // Update dimensions when container resizes
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newWidth = Math.max(400, rect.width || 400);
        setDimensions({ width: newWidth, height: 150 });
      }
    };

    // Initial dimension calculation
    updateDimensions();

    // Add resize listener
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const { width, height } = dimensions;

  // Memoized SVG path generation with dynamic dimensions
  const { linePath, areaPath, processedPoints } = useMemo(() => {
    if (data.points.length === 0) {
      return { linePath: '', areaPath: '', processedPoints: [] };
    }

    const { path: linePath, processedPoints } = generateSvgPath(data.points, width, height);
    const areaPath = generateAreaPath(data.points, width, height);

    return { linePath, areaPath, processedPoints };
  }, [data.points, width, height, generateSvgPath, generateAreaPath]);

  // Use extracted tooltip hook
  const { tooltip, svgRef, handleMouseMove, handleMouseLeave } = useGraphTooltip(processedPoints);

  const formatValue = (value: number) => `${value.toFixed(1)}%`;

  return (
    <>
      <div
        ref={containerRef}
        className={`monitoring-graph ${className}`}
        role="img"
        aria-label="Live uptime metrics graph showing current system health"
      >
        <div className="graph-container">
          <svg
            ref={svgRef}
            className="graph-svg"
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-labelledby="graph-title graph-value"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <title id="graph-title">Uptime Vibes Chart</title>
            <desc>A real-time line chart showing system uptime percentage over time</desc>

            {/* Gradient Definition */}
            <defs>
              <linearGradient id="graphGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style={{ stopColor: 'var(--primary)', stopOpacity: 0.3 }} />
                <stop offset="100%" style={{ stopColor: 'var(--primary)', stopOpacity: 0.05 }} />
              </linearGradient>
            </defs>

            {/* Area under the curve */}
            <path
              d={areaPath}
              fill="url(#graphGradient)"
            />

            {/* Main line */}
            <path
              className="graph-line"
              d={linePath}
              stroke="var(--primary)"
              strokeWidth="2"
              fill="none"
            />

            {/* Hover point indicator */}
            {tooltip.visible && tooltip.point && (
              <circle
                cx={tooltip.point.x}
                cy={tooltip.point.y}
                r="4"
                fill="var(--primary)"
                stroke="#ffffff"
                strokeWidth="2"
                style={{
                  opacity: 1,
                  transition: 'opacity 0.2s ease'
                }}
              />
            )}
          </svg>

          <div className="graph-labels">
            <span className="graph-title">⚡ Uptime Vibes</span>
            <span className="graph-value" id="graph-value" aria-live="polite">
              {formatValue(data.currentValue)}
            </span>
          </div>
        </div>
      </div>

      {/* Rich tooltip like v1 */}
      {tooltip.visible && tooltip.point && (
        <div
          className="graph-tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            background: 'rgba(30, 41, 59, 0.95)',
            border: '1px solid var(--primary)',
            borderRadius: '6px',
            padding: '8px 12px',
            fontSize: '12px',
            color: 'var(--foreground)',
            pointerEvents: 'none',
            zIndex: 1000,
            whiteSpace: 'nowrap',
            fontFamily: 'Courier New, monospace',
            opacity: 1,
            transform: 'translateY(0)',
            transition: 'opacity 0.2s ease, transform 0.2s ease'
          }}
        >
          <div>⚡ Uptime: {tooltip.point.value.toFixed(2)}%</div>
          <div>🕐 Time: {new Date(tooltip.point.timestamp).toLocaleTimeString()}</div>
          <div>📊 Point: {tooltip.pointIndex + 1}/{data.points.length}</div>
        </div>
      )}
    </>
  );
};
